/**
 * sentryPolicy — решение «слать ли события в Sentry», отдельно от инициализации.
 *
 * Вынесено из initSentry, чтобы правило было проверяемым: сам init дёргает
 * @sentry/react и в тестах не запускается.
 */

export interface SentryPolicyInput {
  /** VITE_SENTRY_DSN. Пустая строка = Sentry не настроен. */
  readonly dsn: string;
  /** import.meta.env.MODE: development / production / preview / … */
  readonly mode: string;
  /** VITE_SENTRY_DEV=1 — принудительно слать из dev (отладка самой интеграции). */
  readonly devOptIn?: boolean;
}

export function shouldInitSentry({ dsn, mode, devOptIn = false }: SentryPolicyInput): boolean {
  if (!dsn) return false;
  // Локальная разработка молчит: события с localhost — это правки в процессе,
  // а не то, что видят гости. Одна незавершённая правка под горячей
  // перезагрузкой легко даёт сотню одинаковых событий и съедает квоту Sentry.
  if (mode === "development" && !devOptIn) return false;
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Фильтр ожидаемых доменных DbError (beforeSend).
//
// Основной фильтр живёт в callRpc (@cafe-os/db): коды из USER_INPUT_CODES
// вообще не капчурятся. Этот слой — страховка для DbError, дошедших до Sentry
// другим путём (unhandled promise rejection из fire-and-forget вызова): такие
// события понижаем до level:info + тег, чтобы очередь ошибок не засорялась
// ожидаемыми бизнес-ограничениями (пример: requires_ack, JAVASCRIPT-REACT-3F).
//
// Классификацию НЕ дублируем: единственный источник — USER_INPUT_CODES в
// @cafe-os/db, наружу он виден как DbError.isExpected. Читаем duck-typing'ом,
// потому что статический импорт @cafe-os/db создал бы цикл пакетов (db лениво
// импортирует observability для captureException).
// ─────────────────────────────────────────────────────────────────────────────

/** Минимальная форма Sentry-события — ровно то, что трогает policy. */
export interface SentryLikeEvent {
  level?: string;
  tags?: Record<string, unknown>;
}

/** DbError из @cafe-os/db с isExpected=true (код из USER_INPUT_CODES). */
export function isExpectedDomainError(ex: unknown): boolean {
  if (!ex || typeof ex !== "object") return false;
  const e = ex as { name?: unknown; isExpected?: unknown };
  return e.name === "DbError" && e.isExpected === true;
}

/**
 * Мутирует событие по месту: ожидаемый доменный DbError → level:info + тег
 * `expected_domain_error` (для фильтров в Sentry UI). Всё остальное — не трогаем;
 * TENANT_SUSPENDED не в USER_INPUT_CODES → isExpected=false → остаётся error.
 */
export function applyDomainErrorPolicy(
  event: SentryLikeEvent,
  originalException: unknown,
): void {
  if (!isExpectedDomainError(originalException)) return;
  event.level = "info";
  event.tags = { ...event.tags, expected_domain_error: "true" };
}
